import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { formGroup, formControl, FormDataType } from '@core/utils/form';
import { defineBlockAdvanceForm } from '../Common/AdvanceSettings';
import { useNonNilObservable } from '@core/utils/hooks/rxjs';
import { useConfigureBlock } from '@modules/course/CourseBuilder/hooks';
import { StatementDrillBlockSettings } from './StatementDrillBlockSettings';
import { CourseBuilder } from '../types';
import { BlockContainer } from '../BlockContainer';
import { Card, CardBody, Row } from 'reactstrap';
import { HTMLService } from '@business/services';
import { R } from '@core/utils/r';
import { Container } from 'reactstrap';
import { Button } from '@core/components/Button';
import './index.scss';
import { SortableContainer, SortableElement } from "react-sortable-hoc";
import arrayMove from "array-move";
import { StatementDrillBlockBloc } from './StatementDrillBlockBloc';
import { useBloc } from '@core/utils/bloc';
import { tap } from 'rxjs/operators';

export enum StatementDrillBlockVariation {
  statementDrill = 'statementDrill',
}

function defineStatementDrillBlockForm() {
  return formGroup({
    stepTitle: formControl<string>({
      placeholder: 'Step Title',
      label: 'Step Title',
      required: true,
      initialValue: 'Step Title',
    }),
    statement: formControl<string>({
      placeholder: 'Statement',
      required: true,
      label: 'Statement',
      initialValue: 'Quam nisi convallis imperdiet porta venenatis ultricies litora netus in pellentesque vulputate laoreet, integer varius metus velit conubia leo purus egestas curabitur cubilia.',
    }),
    advance: defineBlockAdvanceForm({ paddingBottom: 30, paddingTop: 30 }),
  });
}

export type StatementDrillForm = ReturnType<typeof defineStatementDrillBlockForm>;
export type StatementDrillFormData = FormDataType<typeof defineStatementDrillBlockForm>;

export const StatementDrillBlock = memo((props: CourseBuilder.BlockProps<StatementDrillBlockVariation>) => {
  const { variation, preview, lessonId } = props;
  const form = useMemo(() => defineStatementDrillBlockForm(), []);
  const formValue = useNonNilObservable(form.value$);
  const [statementItems, setStatementItems] = useState<string[]>([]);
  const [isStarted, setIsStarted] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const [wrongAnswer, setWrongAnswer] = useState(false);
  const [isCompleted, setisCompleted] = useState(false);
  const bloc = useBloc(StatementDrillBlockBloc, lessonId);

  useConfigureBlock({
    props,
    form,
    contentComponent: StatementDrillBlockSettings,
    contentProps: { form, variation },
  });

  useEffect(() => {
    setStatementItems(R.shuffle((HTMLService.strip(formValue.statement)).split(' ')));
  }, [formValue]);

  const onSortEnd = ({ oldIndex, newIndex }: { oldIndex: number, newIndex: number }) => {
    setStatementItems(arrayMove(statementItems, oldIndex, newIndex));
  };

  const checkAnswer = useCallback(() => {
    let answer = (HTMLService.strip(formValue.statement)).split(' ')
    let result = statementItems.length === answer.length && statementItems.every((obj, index) => obj === answer[index]);

    if (result) {
      if (props.editable || props.preview || R.isEmpty(lessonId)) return setisCompleted(true);
      bloc
        .onProgress(lessonId || '', {
          title: formValue.stepTitle,
          answer: statementItems.toString,
          correct: formValue.statement,
          status: result ? 'pass' : 'fail',
        })
        .pipe(tap(() => setisCompleted(true)))
        .subscribe();
    }
    else {
      if (props.editable || props.preview || R.isEmpty(lessonId)) return setWrongAnswer(true), setShowAnswer(true);
      bloc
        .onProgress(lessonId || '', {
          title: formValue.stepTitle,
          answer: statementItems.toString,
          correct: formValue.statement,
          status: result ? 'pass' : 'fail',
        })
        .pipe(tap(() => { setWrongAnswer(true); setShowAnswer(true) }))
        .subscribe();
    }
  }, [wrongAnswer, showAnswer, formValue, statementItems])

  return (
    <Container>
      <BlockContainer advanceForm={formValue.advance}>
        <Card className='mt-4'>
          <CardBody>
            <h1 className='mb-3'>
              {HTMLService.toReact(formValue.stepTitle)}
            </h1>
            <hr />
            {!isCompleted ?
              <div>
                {!isStarted || showAnswer ?
                  <div>
                    {!wrongAnswer ?
                      <p>Learn the statement below so you can recite it without looking at it and then click "Start Drill":</p>
                      :
                      <p>Let's try that again. Review the statement below and then click Continue</p>
                    }
                    <p>{HTMLService.toReact(formValue.statement)}</p>
                    <Button type="button" onClick={() => { setIsStarted(true); setShowAnswer(false) }}>
                      {!showAnswer ? 'Start Drill' : 'Continue'}
                    </Button>
                  </div>
                  :
                  <div>
                    <p>Drag and drop the order of the words below to match the statement.</p>
                    <Row>
                      <SortableList axis={"xy"} items={statementItems} onSortEnd={onSortEnd} />
                    </Row>
                    <div className="mt-4 d-flex justify-content-between">
                      <Button type="button" onClick={() => setShowAnswer(true)} >
                        <i className="mdi font-size-16 mdi-eye mr-2"></i>Show Answer
                      </Button>
                      <Button type="button" onClick={() => checkAnswer()} >
                        Mark Complete
                      </Button>
                    </div>
                  </div>
                }
              </div>
              :
              <div>
                <p>
                  Good job!
                </p>
                <Button type="button" onClick={() => { setIsStarted(false); setShowAnswer(false); setisCompleted(false); setStatementItems(R.shuffle((HTMLService.strip(formValue.statement)).split(' '))) }}>
                  Redo Drill
                </Button>
              </div>
            }
          </CardBody>
        </Card>
      </BlockContainer>
    </Container>
  );
});

interface SortableListItemProps {
  item: string;
}

const SortableListItem = SortableElement(({ item }: SortableListItemProps) => {
  return <span className="training_statement_word word">{item}</span>;
});

interface SortableListProps {
  items: string[];
}

const SortableList = SortableContainer(({ items }: SortableListProps) => {
  return (
    <div className='inline'>
      {items.map((item, index) => {
        return (
          <SortableListItem key={index} index={index} item={item} />
        );
      })}
    </div>
  );
});